import { z } from 'zod';

import { providerLabel } from '~/lib/notification/providerKeyForm';
import { messageSendsOn } from '~/lib/notification/templateForm';
import type {
  NotificationChannel,
  ProviderKey,
  SendMessageRequest,
  Template,
} from '~/modules/query/notification/types';

/** The browser channel is not offered: the Worker skips it. */
export type SendChannel = Extract<NotificationChannel, 'email' | 'sms'>;
export const SEND_CHANNELS: SendChannel[] = ['email', 'sms'];

export interface SendFormValues {
  channels: SendChannel[];
  recipientEmail: string;
  recipientMobile: string;
  /** On: the message names no customer or sender, so the Worker picks the default keys. */
  useDefaultProvider: boolean;
  /** Off-default only: the provider key each channel should go through. */
  emailProviderKeyId: string;
  smsProviderKeyId: string;
  /** `custom` sends the typed messages; `template` sends a template's live version. */
  messageSource: 'custom' | 'template';
  /** Template mode: the picked template record (its key is what goes on the message). */
  templateId: string;
  /** Template mode, default provider, a template for several customers: the one to send as. */
  templateCustomerId: string;
  subject: string;
  emailMessage: string;
  smsMessage: string;
  /** JSON object of template data, delivered on the recipient. */
  data: string;
  /** 0-9; the Worker sends higher first. 0 is the service default. */
  priority: string;
}

export const initialSendForm: SendFormValues = {
  channels: ['email'],
  recipientEmail: '',
  recipientMobile: '',
  useDefaultProvider: true,
  emailProviderKeyId: '',
  smsProviderKeyId: '',
  messageSource: 'custom',
  templateId: '',
  templateCustomerId: '',
  subject: '',
  emailMessage: '',
  smsMessage: '',
  data: '',
  priority: '0',
};

const blank = (value: string) => !value.trim();

/** The picker's choices: the Worker takes queued messages highest priority first, then oldest first. */
export const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'First in, first out' },
  { value: '5', label: 'Higher priority' },
  { value: '9', label: 'ASAP' },
];

/** The parsed object, or a readable reason the text is not a JSON object. Blank is no data. */
function parseData(text: string): { value?: Record<string, unknown>; error?: string } {
  if (blank(text)) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'Data is not valid JSON. Check for a missing quote, comma or brace.' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { error: 'Data must be a JSON object, like {"name": "Sam"}.' };
  }
  return { value: parsed as Record<string, unknown> };
}

/** The address inside `Name <a@b.co>`, or the whole value. */
const bareAddress = (from: string) => (/<([^>]+)>/.exec(from)?.[1] ?? from).trim();

/** The domain the Worker routes an email sender by (the service's `resolveSenderDomain`). */
const senderDomain = (from: string) => bareAddress(from).split('@')[1]?.toLowerCase() ?? '';

/**
 * What a customer's own message carries to land on `key`, since `POST /messages` cannot name a key:
 * the key's customer, plus — for a keyed route — the sender the Worker matches on (the email
 * domain via the key's default from, or the sms number itself). A default key needs no sender:
 * the Worker then uses its default from.
 */
export function providerRouting(
  channel: SendChannel,
  key: ProviderKey,
): { customerId?: string; sender?: { email?: string; number?: string }; error?: string } {
  const customerId = key.customerId ?? undefined;
  if (!key.channelKey) return { customerId };
  if (channel === 'sms') return { customerId, sender: { number: key.channelKey } };
  if (!key.defaultFrom || senderDomain(key.defaultFrom) !== key.channelKey.toLowerCase()) {
    return {
      customerId,
      error: `This key routes mail from ${key.channelKey}, but its default from is not at that domain. Set one on the key to test it.`,
    };
  }
  return { customerId, sender: { email: key.defaultFrom } };
}

/** "SendGrid · mail.acme.com · customer c1": a key as the picker lists it. */
export const providerKeyOptionLabel = (key: ProviderKey): string =>
  [
    providerLabel(key.provider),
    key.channelKey ?? 'default',
    key.customerId ? `customer ${key.customerId}` : 'all customers',
  ].join(' · ');

/** The active keys a channel can be tested through. */
export const sendableKeys = (keys: readonly ProviderKey[], channel: SendChannel): ProviderKey[] =>
  keys.filter((key) => key.channel === channel && key.isActive);

const pickedKey = (
  form: Pick<SendFormValues, 'emailProviderKeyId' | 'smsProviderKeyId'>,
  channel: SendChannel,
  keys: readonly ProviderKey[],
) =>
  keys.find(
    (key) => key.id === (channel === 'email' ? form.emailProviderKeyId : form.smsProviderKeyId),
  );

/** Templates a test can send: only a live one sends anything. */
export const sendableTemplates = (templates: readonly Template[]): Template[] =>
  templates.filter((template) => template.publishedVersion !== null);

const customersLabel = (ids: readonly string[]): string =>
  ids.length === 0
    ? 'all customers'
    : ids.length === 1
      ? `customer ${ids[0]}`
      : `${ids.length} customers`;

/** "Welcome · welcome_email · customer acme": a template as the picker lists it. */
export const templateOptionLabel = (template: Template): string =>
  `${template.name} · ${template.templateKey} · ${customersLabel(template.customerIds)}`;

/** Whether the form must say which of the template's customers it sends as. */
export const needsTemplateCustomer = (
  form: Pick<SendFormValues, 'useDefaultProvider'>,
  template: Template | undefined,
): boolean => form.useDefaultProvider && (template?.customerIds.length ?? 0) > 1;

/** The template's sample data for the first selected channel that has some, as editable JSON; '' when none. */
export function templateSampleData(template: Template, channels: readonly SendChannel[]): string {
  const sample =
    channels.map((channel) => template.message[channel]?.sampleData).find(Boolean) ??
    template.sampleData;
  return sample ? JSON.stringify(sample, null, 2) : '';
}

const pickedTemplate = (
  form: Pick<SendFormValues, 'messageSource' | 'templateId'>,
  templates: readonly Template[],
) =>
  form.messageSource === 'template' ? templates.find((t) => t.id === form.templateId) : undefined;

/**
 * The customer the message carries: the picked keys' customer; else, for a customer template, its
 * one customer or the one picked to send as. Without it the Worker would pick the global template.
 */
function messageCustomer(
  form: SendFormValues,
  keys: readonly ProviderKey[],
  templates: readonly Template[],
): string | undefined {
  let customerId: string | undefined;
  if (!form.useDefaultProvider) {
    for (const channel of form.channels) {
      const key = pickedKey(form, channel, keys);
      if (key) customerId ??= providerRouting(channel, key).customerId;
    }
  }
  if (customerId) return customerId;
  const ids = pickedTemplate(form, templates)?.customerIds ?? [];
  return ids.length === 1
    ? ids[0]
    : ids.includes(form.templateCustomerId)
      ? form.templateCustomerId
      : undefined;
}

/** The service's `validateNotificationRequest` rules, restated per form field, plus the provider and template picks. */
export const sendFormSchema = (keys: readonly ProviderKey[], templates: readonly Template[] = []) =>
  z
    .object({
      channels: z.array(z.enum(['email', 'sms'])),
      recipientEmail: z.string(),
      recipientMobile: z.string(),
      emailMessage: z.string(),
      smsMessage: z.string(),
      messageSource: z.enum(['custom', 'template']),
      templateId: z.string(),
      templateCustomerId: z.string(),
      useDefaultProvider: z.boolean(),
      emailProviderKeyId: z.string(),
      smsProviderKeyId: z.string(),
      data: z.string(),
      priority: z.string(),
    })
    .loose()
    .superRefine((values, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });
      const email = values.channels.includes('email');
      const sms = values.channels.includes('sms');

      if (!email && !sms) issue('channels', 'Pick at least one channel.');
      if (email && blank(values.recipientEmail)) {
        issue('recipientEmail', 'Enter the recipient email address for the email channel.');
      }
      if (sms && blank(values.recipientMobile)) {
        issue('recipientMobile', 'Enter the recipient mobile number for the sms channel.');
      }
      if (values.messageSource === 'custom') {
        if (email && blank(values.emailMessage)) issue('emailMessage', 'Enter the email message.');
        if (sms && blank(values.smsMessage)) issue('smsMessage', 'Enter the sms message.');
      } else {
        const template = pickedTemplate(values, templates);
        if (!template) {
          issue('templateId', 'Pick the template to send.');
        } else {
          const missing = values.channels.filter(
            (channel) => !messageSendsOn(template.message, channel),
          );
          if (missing.length) {
            issue(
              'templateId',
              `This template has no ${missing.map((c) => (c === 'email' ? 'email' : 'SMS')).join(' or ')} message. Pick another template or untick that channel.`,
            );
          }
          const keyCustomer = values.useDefaultProvider
            ? undefined
            : values.channels.map((c) => pickedKey(values, c, keys)?.customerId).find(Boolean);
          const ids = template.customerIds;
          if (ids.length && keyCustomer && !ids.includes(keyCustomer)) {
            issue(
              'templateId',
              `This template is for ${ids.length === 1 ? 'customer' : 'customers'} ${ids.join(', ')}, but the provider key is for customer ${keyCustomer}.`,
            );
          }
          if (needsTemplateCustomer(values, template) && !ids.includes(values.templateCustomerId)) {
            issue('templateCustomerId', 'Pick the customer to send this template as.');
          }
        }
      }

      if (!values.useDefaultProvider) {
        const customers = new Set<string | null>();
        for (const channel of values.channels) {
          const field = channel === 'email' ? 'emailProviderKeyId' : 'smsProviderKeyId';
          const key = pickedKey(values, channel, keys);
          if (!key) {
            issue(
              field,
              `Pick the ${channel === 'email' ? 'email' : 'sms'} provider key to send through.`,
            );
            continue;
          }
          const routing = providerRouting(channel, key);
          if (routing.error) issue(field, routing.error);
          customers.add(key.customerId);
          if (customers.size > 1) {
            issue(field, 'Pick keys for the same customer: one message carries one customer ID.');
          }
        }
      }

      const { error } = parseData(values.data);
      if (error) issue('data', error);

      const priority = values.priority.trim();
      if (priority && !/^\d$/.test(priority)) {
        issue('priority', 'Priority must be a whole number from 0 to 9.');
      }
    });

/**
 * The `POST /messages` body for a form that passed `sendFormSchema`. A picked key's routing rides
 * on `customerId` and `sender` (`providerRouting`); the default leaves both out, though a customer
 * template still carries its customer so the Worker finds it. A template sends only its key — the
 * Worker fills each channel from the live version. The data goes on `recipients[0].data` (the
 * Worker renders from it). Only the selected channels' fields are sent.
 */
export function toSendRequest(
  form: SendFormValues,
  keys: readonly ProviderKey[] = [],
  templates: readonly Template[] = [],
): SendMessageRequest {
  const email = form.channels.includes('email');
  const sms = form.channels.includes('sms');
  const text = (value: string) => value.trim() || undefined;

  let sender: { email?: string; number?: string } = {};
  if (!form.useDefaultProvider) {
    for (const channel of form.channels) {
      const key = pickedKey(form, channel, keys);
      if (key) sender = { ...sender, ...providerRouting(channel, key).sender };
    }
  }
  const customerId = messageCustomer(form, keys, templates);
  const template = pickedTemplate(form, templates);
  const data = parseData(form.data).value;
  const subject = email ? text(form.subject) : undefined;

  return {
    channels: form.channels,
    ...(customerId ? { customerId } : {}),
    ...(text(form.priority) ? { priority: Number(form.priority) } : {}),
    ...(Object.keys(sender).length ? { sender } : {}),
    content: template
      ? { templateKey: template.templateKey }
      : {
          ...(subject ? { subject } : {}),
          message: {
            ...(email ? { email: form.emailMessage } : {}),
            ...(sms ? { sms: form.smsMessage } : {}),
          },
        },
    recipients: [
      {
        ...(email ? { email: form.recipientEmail.trim() } : {}),
        ...(sms ? { mobile: form.recipientMobile.trim() } : {}),
        ...(data ? { data } : {}),
      },
    ],
  };
}
